import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO } from "@/lib/assets";
import { useSite, waLink, resolveImg } from "@/lib/content";

const NAV_ITEMS_BASE = [
  { type: "route", to: "/", label: "Accueil", end: true, testid: "nav-accueil" },
  { type: "route", to: "/galerie", label: "Galerie", testid: "nav-galerie" },
  { type: "route", to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { content } = useSite();
  const whatsapp = waLink(content);
  const menuUrl = resolveImg(content.menu_pdf_url || "/menu-la-matze.pdf");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const desktopLinkCls = ({ isActive }) =>
    `lm-nav-link text-[12px] xl:text-[13px] tracking-[0.22em] uppercase font-medium transition-colors ${
      isActive ? "text-[#8A1C29]" : "text-[#1A1A18] hover:text-[#8A1C29]"
    }`;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
          scrolled
            ? "bg-[#F7F3E2] border-[#8A1C29]/20 shadow-[0_2px_20px_-8px_rgba(26,26,24,0.15)]"
            : "bg-[#FDFBF7]/95 md:bg-[#FDFBF7]/60 backdrop-blur-xl md:backdrop-blur-md border-[#8A1C29]/10 md:border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 shrink-0 relative z-10"
            onClick={() => setOpen(false)}
          >
            <img src={LOGO} alt="La Matze" className="h-9 lg:h-10 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            <NavLink to="/" end data-testid="nav-accueil" className={desktopLinkCls}>Accueil</NavLink>
            <a
              href={menuUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="nav-menu"
              className="lm-nav-link text-[12px] xl:text-[13px] tracking-[0.22em] uppercase font-medium text-[#1A1A18] hover:text-[#8A1C29] transition-colors"
            >
              Menu
            </a>
            <NavLink to="/galerie" data-testid="nav-galerie" className={desktopLinkCls}>Galerie</NavLink>
            <NavLink to="/contact" data-testid="nav-contact" className={desktopLinkCls}>Contact</NavLink>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="nav-cta-reserver"
              className="!hidden lg:!inline-flex lm-btn-primary !py-3 !px-5 !text-[11px]"
            >
              Réserver
            </a>

            <a
              href={`tel:${content.phone_tel || "+41227327961"}`}
              data-testid="nav-mobile-phone"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-[#8A1C29]/30 text-[#8A1C29] hover:bg-[#8A1C29] hover:text-[#FDFBF7] transition-colors"
              aria-label="Appeler La Matze"
            >
              <Phone size={16} strokeWidth={1.7} />
            </a>
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="nav-mobile-cta"
              className="lg:hidden inline-flex items-center justify-center h-10 px-4 bg-[#8A1C29] text-[#FDFBF7] text-[11px] tracking-[0.14em] uppercase font-semibold hover:bg-[#6B141E] transition-colors"
            >
              Réserver
            </a>
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 bg-[#1A1A18] text-[#FDFBF7] hover:bg-[#8A1C29] transition-colors"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
            >
              {open ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden fixed inset-0 z-[60] bg-[#FDFBF7] flex flex-col overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 border-b border-[#E8E2D9] shrink-0">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3"
              data-testid="m-nav-logo"
            >
              <img src={LOGO} alt="La Matze" className="h-9 w-auto" />
            </Link>
            <button
              type="button"
              data-testid="m-nav-close"
              className="inline-flex items-center justify-center h-10 w-10 bg-[#1A1A18] text-[#FDFBF7] hover:bg-[#8A1C29] transition-colors"
              onClick={() => setOpen(false)}
              aria-label="Fermer le menu"
            >
              <X size={18} strokeWidth={2} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col px-6 sm:px-8 py-6">
            <NavLink
              to="/"
              end
              data-testid="m-nav-accueil"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `py-5 border-b border-[#E8E2D9] font-serif-display text-2xl transition-colors ${
                  isActive ? "text-[#8A1C29]" : "text-[#1A1A18] hover:text-[#8A1C29]"
                }`
              }
            >
              Accueil
            </NavLink>
            <a
              href={menuUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="m-nav-menu"
              onClick={() => setOpen(false)}
              className="py-5 border-b border-[#E8E2D9] font-serif-display text-2xl text-[#1A1A18] hover:text-[#8A1C29] transition-colors flex items-center justify-between"
            >
              Menu
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A1C29]">PDF</span>
            </a>
            {NAV_ITEMS_BASE.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`m-${l.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-5 border-b border-[#E8E2D9] font-serif-display text-2xl transition-colors ${
                    isActive ? "text-[#8A1C29]" : "text-[#1A1A18] hover:text-[#8A1C29]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="m-nav-cta-reserver"
              onClick={() => setOpen(false)}
              className="lm-btn-primary mt-8 justify-center w-full !py-4 !text-[12px]"
            >
              Réserver via WhatsApp
            </a>

            <div className="mt-10 border-t border-[#E8E2D9] pt-8 space-y-4 text-[14px]">
              <a
                href={`tel:${content.phone_tel || "+41227327961"}`}
                data-testid="m-nav-phone"
                className="flex items-center gap-3 text-[#1A1A18] hover:text-[#8A1C29] transition-colors"
              >
                <span className="w-9 h-9 flex items-center justify-center border border-[#8A1C29]/30 text-[#8A1C29] shrink-0">
                  <Phone size={14} strokeWidth={1.6} />
                </span>
                {content.phone_display || "+41 22 732 79 61"}
              </a>
              <p className="text-[13px] text-[#5C544F] leading-relaxed pl-12">
                {content.address || "Rue Chaponnière 7 · 1201 Genève"}<br />
                Lundi – Samedi · 10h – 22h
              </p>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
